/**
 * Hook pour récupérer les catégories qui ont au moins un produit en BDD
 * Utilisé pour filtrer dynamiquement la navbar et la page catalogue
 */
import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'

// Cache en mémoire (partagé entre tous les composants)
let categoriesCache = null
let cacheTimestamp = null
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

/**
 * Liste complète des catégories possibles (slugs)
 */
const ALL_CATEGORIES = [
  'vin',
  'biere',
  'spiritueux',
  'bocaux',
  'soft-drinks',
  'huiles-sauces',
  'pet',
  'decorees'
]

/**
 * Hook qui retourne les catégories ayant au moins un produit
 * @returns {{ categories: string[], loading: boolean, error: Error|null }}
 */
export function useCategoriesWithProducts() {
  const [categories, setCategories] = useState(categoriesCache || [])
  const [loading, setLoading] = useState(!categoriesCache)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchCategories = async () => {
      // Vérifier le cache
      const now = Date.now()
      if (categoriesCache && cacheTimestamp && (now - cacheTimestamp < CACHE_DURATION)) {
        setCategories(categoriesCache)
        setLoading(false)
        return
      }

      try {
        setLoading(true)

        // Récupérer tous les produits avec leurs catégories
        const { data, error: fetchError } = await supabase
          .from('products')
          .select('category, categories')

        if (fetchError) throw fetchError

        // Extraire toutes les catégories uniques qui ont des produits
        const categoriesWithProducts = new Set()

        data.forEach(product => {
          // Support multi-catégories (array)
          if (product.categories && Array.isArray(product.categories)) {
            product.categories.forEach(cat => {
              if (cat) categoriesWithProducts.add(cat.toLowerCase())
            })
          }
          // Fallback sur l'ancien champ category (string)
          if (product.category) {
            categoriesWithProducts.add(product.category.toLowerCase())
          }
        })

        // Filtrer pour ne garder que les catégories valides
        const validCategories = ALL_CATEGORIES.filter(cat =>
          categoriesWithProducts.has(cat)
        )

        // Mettre en cache
        categoriesCache = validCategories
        cacheTimestamp = now

        setCategories(validCategories)
        setError(null)
      } catch (err) {
        console.error('Erreur lors du chargement des catégories:', err)
        setError(err)
        // En cas d'erreur, afficher toutes les catégories (fallback)
        setCategories(ALL_CATEGORIES)
      } finally {
        setLoading(false)
      }
    }

    fetchCategories()
  }, [])

  return { categories, loading, error }
}

/**
 * Invalide le cache des catégories (à appeler lors d'ajout/modification de produits)
 */
export function clearCategoriesCache() {
  categoriesCache = null
  cacheTimestamp = null
}

export default useCategoriesWithProducts
